import React, { createContext, useState, useContext, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Achievement } from '../types/index';

interface Notification {
  id: string;
  achievement: Achievement;
}

interface NotificationContextType {
  notifications: Notification[];
  showAchievement: (achievement: Achievement) => void;
  removeNotification: (id: string) => void;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    // Load saved preference or default to true
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const showAchievement = useCallback((achievement: Achievement) => {
    // Only show notification if enabled
    if (!notificationsEnabled) return;
    
    const newNotification: Notification = {
      id: Date.now().toString(),
      achievement
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Auto-remove after 6 seconds (1 second longer than the animation)
    setTimeout(() => {
      removeNotification(newNotification.id);
    }, 6000);
  }, [notificationsEnabled]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const handleSetNotificationsEnabled = useCallback((enabled: boolean) => {
    setNotificationsEnabled(enabled);
    localStorage.setItem('notificationsEnabled', enabled.toString());
  }, []);

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      showAchievement, 
      removeNotification,
      notificationsEnabled,
      setNotificationsEnabled: handleSetNotificationsEnabled
    }}>
      {children}
    </NotificationContext.Provider>
  );
};